import useLanguage from '@op/hooks/useLanguage';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import {
  DEFAULT_REPORT_OWNER,
  SAVED_LIST_DEFAULT_REPORT_OWNER,
  type TReportOwner,
} from '@op/components/ReportPermission';
import { TabType } from '@op/pages/AITaskCenter/hooks';
import { downloadAction } from '@op/pages/download/utils';
import {
  deleteAIAnalysisSummary,
  deleteUserSummary,
  updateKeywordAnalysisTask,
  updateRepeatSummaryTask,
} from '@op/services';
import { DBO_U } from '@op/utils/format';
import React from 'react';
import { Modal, notification } from 'tea-component';
import { useHistory } from 'umi';

export function useDeleteSummaryItem({
  finallyCallback,
  thenCallback,
}: {
  finallyCallback?: () => void;
  thenCallback?: () => void;
}) {
  const { formatMessage } = useLanguage();

  function getDeleteDescription({ name }: { name: string }) {
    return {
      description: formatMessage(
        {
          id: 'Delete {name}? Deletion is irreversible, please proceed with caution.',
        },
        { name: <b>{name}</b> },
      ),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    };
  }

  function successTips() {
    notification.success({
      description: formatMessage({ id: 'Delete success' }),
    });
  }

  function failTips() {
    notification.error({
      description: formatMessage({ id: 'Delete failed' }),
    });
  }
  /**
   * 普通GPT报告的删除
   * @param filter
   * @returns
   */
  async function deleteCommentSummary(filter: {
    edition_unified_id: any;
    name: string;
    id: string;
    entity_type: string;
    id_type: string;
  }) {
    if (!filter.edition_unified_id) return false;
    const { name, id, edition_unified_id, entity_type, id_type } = filter;
    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Delete Analysis?' }),
      ...getDeleteDescription({ name }),
    });

    if (yes) {
      deleteAIAnalysisSummary({
        id: Number(id),
        edition_unified_id,
        entity_type,
        id_type,
      })
        .then(() => {
          thenCallback?.();
          successTips();
        })
        .catch(() => {
          failTips();
        })
        .finally(() => {
          finallyCallback?.();
        });
    }
  }

  /**
   * 订阅的GPT报告删除
   * @param filter
   * @returns
   */
  async function deleteSubscriptionSummary(filter: {
    cycle_end_date: string;
    name: string;
    id: string;
  }) {
    if (!filter.id) return false;
    const { name, id, cycle_end_date } = filter;
    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Delete Repeat Task?' }),
      ...getDeleteDescription({ name }),
    });

    if (yes) {
      updateRepeatSummaryTask({
        id: Number(id),
        name,
        cycle_end_date,
        status: 0,
      })
        .then(() => {
          thenCallback?.();
          successTips();
        })
        .catch(() => {
          failTips();
        })
        .finally(() => {
          finallyCallback?.();
        });
    }
  }

  /**
   * 删除Keyword的分析报告
   * @param filter
   * @returns
   */
  async function deleteKeywordAndUrlSummary(filter: {
    id_type: any;
    id: any;
    entity_type: any;
    edition_unified_id: any;
    name: string;
  }) {
    if (!filter.edition_unified_id) return false;
    const { name } = filter;
    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Delete Analysis?' }),
      ...getDeleteDescription({ name }),
    });

    if (yes) {
      deleteUserSummary({
        edition_unified_id: filter.edition_unified_id,
        entity_type: filter.entity_type,
        id: filter.id,
        id_type: filter.id_type,
      })
        .then(() => {
          thenCallback?.();
          successTips();
        })
        .catch(() => {
          failTips();
        })
        .finally(() => {
          finallyCallback?.();
        });
    }
  }

  async function deleteKeywordTopicSubscriptionSummary(filter: any) {
    if (!filter.game_id) return false;
    const { name } = filter;
    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Delete Analysis?' }),
      ...getDeleteDescription({ name }),
    });
    if (yes) {
      updateKeywordAnalysisTask({
        id: filter.id,
        keyword_analysis_task_type: 'recurring',
        status: 'stopped',
      })
        .then(() => {
          thenCallback?.();
          successTips();
        })
        .catch(() => {
          failTips();
        })
        .finally(() => {
          finallyCallback?.();
        });
    }
  }

  return {
    deleteCommentSummary,
    deleteSubscriptionSummary,
    deleteKeywordAndUrlSummary,
    deleteKeywordTopicSubscriptionSummary,
  };
}
export type RecordsType = {
  id: string;
  task_id: string;
  short_url: string;
  name: string;
  task_type: string;
  model_type: string;
  gameInfo: any;
  category: 'common' | 'keyword' | 'url';
  game_id: string;
  cycle_end_date: string;
  lead_id?: number;
  version_id?: number;
  data_type: 'new' | 'legacy';
  files?: {
    summary_excel_url_en?: string;
    summary_excel_url_zh?: string;
    summary_md_url_en?: string;
    summary_md_url_zh?: string;
  };
};

type TReportRedirectParams = {
  subtitle?: string;
  owner?: TReportOwner;
};

function buildOwnerQuery(owner?: TReportOwner) {
  if (
    !owner ||
    owner === DEFAULT_REPORT_OWNER ||
    owner === SAVED_LIST_DEFAULT_REPORT_OWNER
  ) {
    return '';
  }
  return `&owner=${owner}`;
}

function getReportRedirect(tab: string, params?: string | TReportRedirectParams) {
  let subtitle = '';
  let owner: TReportOwner | undefined;

  if (typeof params === 'string') {
    subtitle = params;
  } else if (params) {
    subtitle = params.subtitle || '';
    owner = params.owner;
  }

  const subtitleUrl = subtitle ? `&subtitle=${subtitle}` : '';
  const ownerUrl = buildOwnerQuery(owner);
  return `redirect=${encodeURIComponent(
    `/opinion/AITaskCenter?tab=${tab}${subtitleUrl}${ownerUrl}`,
  )}`;
}

export function usePreviewSummaryItem(props?: { isSubscription?: boolean }) {
  const history = useHistory();
  const { isSubscription = false } = props || {};

  function previewKeyword(
    record: RecordsType,
    tab?: TabType,
    source?: TabType,
    owner?: TReportOwner,
  ) {
    const {
      id,
      game_id,
      lead_id = 0,
      data_type = 'legacy',
      version_id,
    } = record;
    const realId = data_type === 'new' && !isSubscription ? lead_id : id;
    buriedBtnPoint('PreviewAnalysis', undefined, String(realId), 'keyword');

    const obj: any = {
      preview: 1,
      data_type,
    };
    if (version_id) {
      obj.version_id = version_id;
    }

    // 旧数据用id下转，新keyword数据用lead_id下转
    const toUrl = `/opinion/AIAnalysisHub/KeywordAnalysis/preview/${realId}?${DBO_U.objToUrl(
      obj,
    )}`;

    if (tab) {
      return DBO_U.open(
        `${toUrl}&gameid=${game_id}&${getReportRedirect(tab, {
          subtitle: source !== tab ? source : '',
          owner,
        })}`,
      );
    }
    history.push(toUrl, { isCurrent: true, source, isSubscription, owner });
  }

  function previewUrl(
    record: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    const { id, game_id, lead_id = 0 } = record;
    buriedBtnPoint('PreviewAnalysis', undefined, String(id), 'url');

    const toUrl = `/opinion/AIAnalysisHub/URLAnalysis/preview/${id}?preview=1&lead_id=${lead_id}`;
    if (tab) {
      return DBO_U.open(
        `${toUrl}&gameid=${game_id}&${getReportRedirect(tab, { owner })}`,
      );
    }
    history.push(toUrl, { isCurrent: true, owner });
  }

  function subscriptAndCommentPreview(
    isSubscript: boolean,
    record: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    const { id, game_id } = record;
    buriedBtnPoint(
      'PreviewAnalysis',
      undefined,
      String(id),
      isSubscript ? 'repeat_summary_task' : 'common',
    );
    const toUrl = `/opinion/AIAnalysisHub/CustomAISummary/preview/${id}?isSubscript=${isSubscript}${buildOwnerQuery(
      owner,
    )}`;

    if (tab) {
      return DBO_U.open(
        `${toUrl}&gameid=${game_id}&${getReportRedirect(tab, { owner })}`,
      );
    }
    history.push(toUrl);
  }

  function previewComment(
    record: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    subscriptAndCommentPreview(false, record, tab, owner);
  }

  function previewSubscript(
    record: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    subscriptAndCommentPreview(true, record, tab, owner);
  }

  return {
    keyword: previewKeyword,
    topic: previewKeyword,
    url: previewUrl,
    common: previewComment,
    repeat_summary_task: previewSubscript,
    keyword_topic: previewKeyword,
  };
}

export function useDownloadSummaryItem(props?: { isSubscription?: boolean }) {
  const { isSubscription = false } = props || {};
  const history = useHistory();

  function downloadKeyword(
    records: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    const {
      id,
      game_id,
      lead_id = 0,
      data_type = 'legacy',
      version_id,
    } = records;
    const realId = data_type === 'new' && !isSubscription ? lead_id : id;

    const toUrl = `/opinion/AIAnalysisHub/KeywordAnalysis/preview/${realId}`;
    buriedBtnPoint('DownloadReport');

    const obj: any = {
      data_type,
      gameid: game_id,
    };
    if (version_id) {
      obj.version_id = version_id;
    }

    const url = `${toUrl}?${DBO_U.objToUrl(obj)}`;

    if (tab) {
      return DBO_U.open(
        `${url}&${getReportRedirect(tab, { owner })}`,
      );
    }
    history.push(url, { isCurrent: true });
  }

  function downloadUrl(
    records: RecordsType,
    tab?: TabType,
    owner?: TReportOwner,
  ) {
    const { id, game_id } = records;
    const toUrl = `/opinion/AIAnalysisHub/URLAnalysis/preview/${id}?gameid=${game_id}`;
    buriedBtnPoint('DownloadReport');

    if (tab) {
      return DBO_U.open(`${toUrl}&${getReportRedirect(tab, { owner })}`);
    }
    history.push(toUrl, { isCurrent: true });
  }

  function subscriptAndCommentDownload(
    isSubscript: boolean,
    records: RecordsType,
  ) {
    const { task_id, task_type, model_type, name, short_url, gameInfo, files } =
      records;
    const gptOptions = String(task_type) === '3' ? 'basic' : 'advanced';
    buriedBtnPoint(
      'DownloadReport',
      isSubscript ? 'One-time Summary' : 'Subscriptions Summary',
    );

    const promptInfo: any = {
      modelType: model_type,
      gptOptions,
      taskId: task_id,
      saveName: name,
      shortId: short_url,
    };

    if (files && Object.keys(files).length > 0) {
      downloadAction({
        key: 'GptFileZipDownload',
        fileName: 'Advanced AI Report Reviews',
        filter: {
          ...gameInfo,
          game_id: gameInfo?.edition_unified_id,
          task_id,
          prompt: promptInfo.prompt,
        },
        auxiliaryParams: { task_id: promptInfo.taskId },
      });
    } else {
      downloadAction({
        key:
          gptOptions === 'advanced' ? 'GptAdvanceDownload' : 'GptCommonDownload',
        filter: {
          ...gameInfo,
          game_id: gameInfo?.edition_unified_id,
          task_id,
          prompt: promptInfo.prompt,
        },
        auxiliaryParams: {
          promptInfo,
          advanceFilter: { ...gameInfo, task_id },
        },
        fileName: gptOptions === 'basic' ? 'AIGC' : 'Advance Gpt',
      });
    }

  }

  function downloadComment(data: RecordsType) {
    subscriptAndCommentDownload(false, data);
  }

  function downloadSubscript(data: RecordsType) {
    subscriptAndCommentDownload(true, data);
  }

  return {
    keyword: downloadKeyword,
    topic: downloadKeyword,
    keyword_topic: downloadKeyword,
    url: downloadUrl,
    common: downloadComment,
    repeat_summary_task: downloadSubscript,
  };
}
